import { useCallback } from 'react';
import { toast } from 'sonner';
import { useItemsStore } from '@/features/timeline/stores/items-store';
import { useKeyframesStore } from '@/features/timeline/stores/keyframes-store';
import { useProjectStore } from '@/features/projects/stores/project-store';
import {
    VIDEO_TEMPLATES,
    getTemplateVisualEffects,
    isDefaultTemplateVignetteEffect,
} from '@/features/editor/data/templates';
import type { VideoTemplate } from '@/types/template';
import type { MediaFitMode } from '@/types/transform';
import type { ItemEffect } from '@/types/effects';
import type { TextItem, TimelineTrack, VideoItem, ImageItem } from '@/types/timeline';
import { createLogger } from '@/lib/logger';
import { useSelectionStore } from '@/features/editor/stores/selection-store';
import { useEditorStore } from '@/features/editor/stores/editor-store';
import { DEFAULT_TRACK_HEIGHT } from '@/features/timeline/constants';
import { buildClipMotionProperties } from '@/utils/clip-motion';
import { useTemplateApplyStore } from './template-apply-store';

const log = createLogger('useTemplateActions');

function yieldToBrowser(): Promise<void> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => resolve());
    });
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

const TEMPLATE_TRACK_NAMES = ['Overlay', 'Background', 'Template Overlay', 'Template Title', 'Template Title 1', 'Template Title 2', 'Template Background'];

const TRACK_ORDER_MAP: Record<string, number> = {
    'Overlay': 0,
    'Title': 1,
    'Title1': 1,
    'Title 1': 1,
    'Title2': 2,
    'Title 2': 2,
    'Caption': 2,
    'Captions': 2,
    'Animation': 99,
    'Visuals': 4,
    'Video/Image': 4,
    'Audio': 102,
    'Background': 100,
    'Template Background': 100,
    'SFX': 103
};

function isTemplateManagedVignette(entry: ItemEffect | undefined) {
    const effect = entry?.effect;
    return (
        effect?.type === 'overlay-effect' &&
        effect.variant === 'vignette' &&
        (entry?.source === 'template' || isDefaultTemplateVignetteEffect(effect))
    );
}

function mergeTemplateVisualEffects(currentEffects: ItemEffect[] | undefined, templateEffects: any[] | undefined) {
    const retainedEffects = (currentEffects || []).filter((entry) => {
        return !isTemplateManagedVignette(entry);
    });

    if (!templateEffects?.length) {
        return retainedEffects.length > 0 ? retainedEffects : undefined;
    }

    return [
        ...retainedEffects,
        ...templateEffects.map((effect) => ({
            id: crypto.randomUUID(),
            effect,
            enabled: true,
            source: 'template' as const,
        })),
    ];
}

const DEFAULT_TEXT_STYLE: {
    fontFamily: string;
    fontWeight: 'bold' | 'normal' | 'medium' | 'semibold';
    color: string;
    backgroundColor: string;
    width?: number;
    height?: number;
    captionY: number;
} = {
    fontFamily: 'Inter',
    fontWeight: 'bold' as const,
    color: '#ffffff',
    backgroundColor: 'rgba(0, 0, 0, 0.85)', // 더 어두운 배경
    // width is now dynamic based on canvas in applyTemplate
    captionY: 0.8
};

function splitIssueHeadline(rawValue: string): { title1: string; title2: string } {
    const raw = rawValue.trim();
    if (!raw) {
        return { title1: '', title2: '' };
    }

    if (raw.includes('\n')) {
        const [firstLine, ...rest] = raw.split('\n');
        return {
            title1: (firstLine || '').trim(),
            title2: rest.join(' ').trim(),
        };
    }

    const normalized = raw.replace(/\s+/g, ' ').trim();
    const midpoint = normalized.length / 2;
    const minSideLength = 6;
    const splitCandidates: number[] = [];

    for (let i = 1; i < normalized.length - 1; i += 1) {
        const currentChar = normalized[i];
        const previousChar = normalized[i - 1];
        const nextChar = normalized[i + 1];
        const currentIsSpace = currentChar === ' ';
        const previousIsPunctuation = /[.,!?;:·ㆍ…]/.test(previousChar);
        const currentIsPunctuation = /[.,!?;:·ㆍ…]/.test(currentChar);

        if (currentIsSpace && i >= minSideLength && normalized.length - i >= minSideLength) {
            splitCandidates.push(i);
            continue;
        }

        if (
            previousIsPunctuation &&
            nextChar !== ' ' &&
            i >= minSideLength &&
            normalized.length - i >= minSideLength
        ) {
            splitCandidates.push(i);
            continue;
        }

        if (
            currentIsPunctuation &&
            i + 1 >= minSideLength &&
            normalized.length - (i + 1) >= minSideLength
        ) {
            splitCandidates.push(i + 1);
        }
    }

    let splitIndex = -1;
    if (splitCandidates.length > 0) {
        splitIndex = splitCandidates.reduce((best, candidate) =>
            Math.abs(candidate - midpoint) < Math.abs(best - midpoint) ? candidate : best
        );
    }

    if (splitIndex <= 0 || splitIndex >= normalized.length) {
        splitIndex = Math.max(minSideLength, Math.min(normalized.length - minSideLength, Math.round(midpoint)));
    }

    return {
        title1: normalized.slice(0, splitIndex).trim(),
        title2: normalized.slice(splitIndex).trim(),
    };
}

export function useTemplateActions() {
    const {
        tracks,
        items,
        setTracks,
        _updateItemsTransform,
        _addItem,
        _removeItems,
        _updateItem
    } = useItemsStore();
    const templateStyles = useEditorStore(s => s.templateStyles);
    const setTemplateStyle = useEditorStore(s => s.setTemplateStyle);
    const activeTemplateId = useEditorStore(s => s.activeTemplateId);
    const setActiveTemplateId = useEditorStore(s => s.setActiveTemplateId);
    const saveCustomTemplate = useEditorStore(s => s.saveCustomTemplate);
    const deleteCustomTemplate = useEditorStore(s => s.deleteCustomTemplate);

    const currentProject = useProjectStore(s => s.currentProject);

    const applyTemplate = useCallback(async (templateId: string) => {
        const latestEditorState = useEditorStore.getState();
        const latestProject = useProjectStore.getState().currentProject;
        const latestTemplateStyles = latestEditorState.templateStyles;
        const latestActiveTemplateId = latestEditorState.activeTemplateId;
        const latestCustomTemplates = latestEditorState.customTemplates;
        const getLatestItems = () => useItemsStore.getState().items;
        const getLatestTracks = () => useItemsStore.getState().tracks;
        const template = VIDEO_TEMPLATES.find(t => t.id === templateId) || latestCustomTemplates.find(t => t.id === templateId);
        if (!template) {
            log.error('Template not found', { templateId });
            return;
        }

        const templateApply = useTemplateApplyStore.getState();
        if (templateApply.isApplying) {
            return;
        }

        templateApply.start(template.name);
        await yieldToBrowser();

        const projectMetadata = latestProject?.metadata || { width: 1080, height: 1920 };
        const canvasWidth = projectMetadata.width;
        const canvasHeight = projectMetadata.height;
        const isLandscape = canvasWidth > canvasHeight;
        const duration = Math.max(...getLatestItems().map(i => i.from + i.durationInFrames), 30 * 10);

        log.info('Applying template', { templateName: template.name, resolution: `${canvasWidth}x${canvasHeight}` });

        // Helper to get latest items/tracks since useItemsStore methods update state asynchronously

        // 1. Identification
        const isBasic = templateId === 'basic' || template.layoutId === 'basic';
        const isIssue = templateId.startsWith('issue') || (template.layoutId && template.layoutId.startsWith('issue'));
        const isSsul = templateId.startsWith('ssul') || (template.layoutId && template.layoutId === 'ssul');
        try {
            templateApply.update(12, '기존 템플릿 요소를 정리하는 중...');
            await yieldToBrowser();

            // 2. Clean up existing template elements on template tracks
            const itemsToRemove: string[] = [];
            getLatestItems().forEach(item => {
                const track = getLatestTracks().find(t => t.id === item.trackId);
                if (track) {
                    if (TEMPLATE_TRACK_NAMES.includes(track.name)) {
                        itemsToRemove.push(item.id);
                    } else if (!template.title && (track.name === 'Title1' || track.name === 'Title2')) {
                        // If the new template doesn't have titles (e.g., Basic), remove old auto-generated titles
                        itemsToRemove.push(item.id);
                    } else if (template.title && !template.title.isDoubleLine && track.name === 'Title2') {
                        // If moving from a double-line title template to single-line, remove the second line
                        itemsToRemove.push(item.id);
                    }
                }
            });

            if (itemsToRemove.length > 0) {
                _removeItems(itemsToRemove);
            }

            templateApply.update(28, '트랙 구성을 정리하는 중...');
            await yieldToBrowser();

            // 3. Track Management
            let newTracks: TimelineTrack[] = JSON.parse(JSON.stringify(getLatestTracks())); // Deep clone

            if (isBasic) {
                newTracks = newTracks.filter(t => !TEMPLATE_TRACK_NAMES.includes(t.name));
                newTracks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                newTracks.forEach((t, i) => {
                    t.order = i;
                });
                setTracks(newTracks);
            } else {
                // Normalize all known tracks to their standard orders and names
                newTracks.forEach((t) => {
                    // Force rename legacy 'Text' or 'Video/Image' tracks first
                    if (t.name === 'Text' || t.name === 'Captions') {
                        t.name = 'Caption';
                    } else if (t.name === 'Video/Image') {
                        t.name = 'Visuals';
                    }

                    // If the track is one of our known ones, give it the standard order
                    if (TRACK_ORDER_MAP[t.name] !== undefined) {
                        t.order = TRACK_ORDER_MAP[t.name] as number;
                    }
                });

                const contentTracks = newTracks
                    .filter(t => !TEMPLATE_TRACK_NAMES.includes(t.name))
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                // For truly custom content tracks that aren't in our standard list, 
                // place them after Visuals (order 4) but before Background (order 100)
                contentTracks.forEach((t, i) => {
                    if (TRACK_ORDER_MAP[t.name] === undefined) {
                        t.order = 5 + i;
                    }
                });

                const getOrCreateTrack = (name: string, order: number, locked = false) => {
                    let track = newTracks.find(t => t.name === name);
                    if (!track) {
                        track = {
                            id: crypto.randomUUID(),
                            name,
                            order,
                            locked,
                            visible: true,
                            muted: false,
                            solo: false,
                            height: DEFAULT_TRACK_HEIGHT,
                            items: []
                        };
                        newTracks.push(track);
                    } else {
                        track.order = order;
                        track.locked = locked;
                    }
                    return track;
                };

                // Only create tracks if template has elements for them
                if (template.overlay) getOrCreateTrack('Overlay', 0, false);
                if (template.title) {
                    getOrCreateTrack('Title1', 1, false);
                    if (template.title.isDoubleLine) {
                        getOrCreateTrack('Title2', 2, false);
                    }
                }
                if (template.background) getOrCreateTrack('Background', 100, false); // Keep background at higher order but within relative bounds

                // Filter out template tracks that are no longer needed
                newTracks = newTracks.filter(t => {
                    if (t.name === 'Overlay' && !template.overlay) return false;
                    if (t.name === 'Background' && !template.background) return false;
                    if (t.name === 'Title1' && !template.title) return false;
                    if (t.name === 'Title2' && (!template.title || !template.title.isDoubleLine)) return false;
                    if (t.name === 'Title') return false; // Legacy cleanup
                    if (t.name === 'Title 1') return false; // Transition cleanup
                    if (t.name === 'Title 2') return false; // Transition cleanup
                    if (t.name.startsWith('Template ')) return false; // Legacy cleanup
                    return true;
                });

                newTracks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                setTracks(newTracks);
            }

        templateApply.update(40, '기준 아이템을 분석하는 중...');
        await yieldToBrowser();

	        // 4. Identify Leaders (Prioritize selected items, fallback to first item)
        const selectedIds = useSelectionStore.getState().selectedItemIds;
        const currentItems = getLatestItems();
        const selectedItems = currentItems.filter(i => selectedIds.includes(i.id));
        const sortedItems = [...currentItems].sort((a, b) => a.from - b.from);

        let leaderVisual = selectedItems.find(i =>
            (i.type === 'video' || i.type === 'image') &&
            !TEMPLATE_TRACK_NAMES.includes(newTracks.find(t => t.id === i.trackId)?.name || '')
        );

        if (!leaderVisual) {
            leaderVisual = sortedItems.find(i =>
                (i.type === 'video' || i.type === 'image') &&
                !TEMPLATE_TRACK_NAMES.includes(newTracks.find(t => t.id === i.trackId)?.name || '')
            );
        }

        let leaderText = selectedItems.find(i =>
            i.type === 'text' &&
            !TEMPLATE_TRACK_NAMES.includes(newTracks.find(t => t.id === i.trackId)?.name || '')
        ) as TextItem | undefined;

        if (!leaderText) {
            leaderText = sortedItems.find(i =>
                i.type === 'text' &&
                !TEMPLATE_TRACK_NAMES.includes(newTracks.find(t => t.id === i.trackId)?.name || '')
            ) as TextItem | undefined;
        }

	        // 4.5 Restore Stored Style if available
	            const savedStyle = latestTemplateStyles[templateId];
	            const textMaster = (savedStyle?.text) || undefined;
	            const templateOptions = useEditorStore.getState().templateOptions;
	            const templateVisualEffects = getTemplateVisualEffects(template, templateOptions);

	        const currentLayoutId = latestActiveTemplateId?.split('-')[0];
	        const nextLayoutId = templateId.split('-')[0];
	        // FORCE layout change reset if moving between major layout types or if no template was active
	        const isLayoutChange = !latestActiveTemplateId || currentLayoutId !== nextLayoutId || latestActiveTemplateId !== templateId;

        // 5. Transform Primary Visuals
        // IGNORE stale saved colors/layouts when switching between different layout types (e.g. Issue -> Ssul)
        const visualMaster = isLayoutChange ? undefined : savedStyle?.visual;
        const currentTracks = getLatestTracks();
        const contentItems = getLatestItems().filter(item => {
            const track = currentTracks.find(t => t.id === item.trackId);
            return track && !TEMPLATE_TRACK_NAMES.includes(track.name) && (item.type === 'video' || item.type === 'image');
        });

        templateApply.update(55, '비주얼 레이아웃을 적용하는 중...');
        await yieldToBrowser();

        for (const [index, item] of contentItems.entries()) {
            const hasMaster = !isBasic && !!visualMaster;
            let newWidth = hasMaster ? visualMaster!.width : (template.layout.width ?? (canvasWidth * template.layout.scale));
            let newHeight = hasMaster ? visualMaster!.height : (template.layout.height ?? (canvasHeight * template.layout.scale));
            let newX = hasMaster ? visualMaster!.x : template.layout.x;
            let newY = hasMaster ? visualMaster!.y : template.layout.y;
            let newObjectFit = hasMaster ? (visualMaster!.objectFit || 'cover') : (template.layout.objectFit || 'contain');

            // Override Ssul/Issue Layouts for Landscape (Long-form)
            if (isLandscape && !hasMaster) {
                // Determine dimensions to fill the landscape screen properly while respecting layout limits
                if (isSsul) {
                    newWidth = canvasWidth * 0.75; // 75% width
                    newHeight = (newWidth / 1.77) + 50;
                    newY = 20;
                    newX = 0;
                    // User asked for '포함' (contain) in longform
                    newObjectFit = 'contain';
                } else if (isIssue) {
                    newWidth = canvasWidth;
                    // Leave space for title or just use height
                    newHeight = canvasHeight - 200;
                    newY = 100; // shift down for title
                    newX = 0;
                    // User asked for '포함' (contain) in longform
                    newObjectFit = 'contain';
                } else {
                    newWidth = canvasWidth;
                    newHeight = canvasHeight;
                    newObjectFit = 'contain';
                }
            }

            _updateItemsTransform([item.id], {
                x: newX,
                y: newY,
                width: newWidth,
                height: newHeight,
                rotation: 0,
                objectFit: newObjectFit as any
            });

            const nextEffects = mergeTemplateVisualEffects(item.effects, templateVisualEffects);
            if (nextEffects !== item.effects) {
                _updateItem(item.id, { effects: nextEffects as any });
            }

            // Add varied clip motion so adjacent scenes don't feel mechanically identical.
            if (item.type === 'image') {
                const motionProperties = buildClipMotionProperties(item, projectMetadata.fps || 30);
                if (motionProperties.length > 0) {
                    useKeyframesStore.getState()._addKeyframes(
                        motionProperties.flatMap((property) =>
                            property.keyframes.map((keyframe) => ({
                                itemId: item.id,
                                property: property.property,
                                frame: keyframe.frame,
                                value: keyframe.value,
                                easing: keyframe.easing,
                            }))
                        )
                    );
                }
            }
            if ((index + 1) % 24 === 0) {
                const progress = 55 + Math.round(((index + 1) / Math.max(contentItems.length, 1)) * 12);
                templateApply.update(progress, '비주얼 레이아웃을 적용하는 중...');
                await yieldToBrowser();
            }
        }

        // 6. Update Text Styles
        // Identify base style: use template style if available, otherwise fallback to DEFAULT_TEXT_STYLE
        let baseStyle = template.textStyle || DEFAULT_TEXT_STYLE;

        // Apply landscape defaults for basic/issue/ssul layouts if they don't have explicit values
        if ((isBasic || isIssue || isSsul) && isLandscape) {
            baseStyle = {
                ...baseStyle,
                width: baseStyle.width ?? 1920,
                height: baseStyle.height ?? 140
            };
        }

        // When switching templates/themes, ALWAYS prioritize the template's own design (color, font)
        let style = (textMaster && !isLayoutChange) ? textMaster : (leaderText && !isLayoutChange) ? {
            ...baseStyle,
            fontFamily: baseStyle?.fontFamily || leaderText.fontFamily,
            fontWeight: (baseStyle?.fontWeight as any) || leaderText.fontWeight, // Safe cast
            color: baseStyle?.color || leaderText.color,
            backgroundColor: baseStyle?.backgroundColor || leaderText.backgroundColor,
            width: ((isBasic || isIssue || isSsul) && isLandscape) ? baseStyle?.width : (leaderText.transform?.width || (baseStyle as any)?.width),
            height: ((isBasic || isIssue || isSsul) && isLandscape) ? baseStyle?.height : (leaderText.transform?.height || (baseStyle as any)?.height),
            opacity: (leaderText.transform as any)?.opacity || (baseStyle as any)?.opacity,
            cornerRadius: (leaderText.transform as any)?.cornerRadius || (baseStyle as any)?.cornerRadius,
            captionX: leaderText.transform?.x !== undefined ? leaderText.transform.x : (baseStyle as any)?.captionX ?? 0,
            captionY: leaderText.transform?.y !== undefined ? leaderText.transform.y : (isSsul && isLandscape) ? 0.40 : (baseStyle as any)?.captionY ?? 0.8,
        } : { ...baseStyle } as VideoTemplate['textStyle'];

        // FORCE landscape basic/issue/ssul overrides to ensure it always fills the screen with 1920
        if ((isBasic || isIssue || isSsul) && isLandscape && style) {
            style.width = canvasWidth;
            style.height = 140;
            if (isSsul) style.captionY = 430; // Move caption to bottom in landscape ssul
        }


        const textItems = getLatestItems().filter(item => item.type === 'text') as TextItem[];
        templateApply.update(72, '텍스트 스타일을 적용하는 중...');
        await yieldToBrowser();

        for (const [index, item] of textItems.entries()) {
            const track = getLatestTracks().find(t => t.id === item.trackId);
            if (track && TEMPLATE_TRACK_NAMES.includes(track.name)) continue;
            if (track && (track.name === 'Title1' || track.name === 'Title2')) continue;

            _updateItem(item.id, {
                fontFamily: style?.fontFamily || item.fontFamily || 'Inter',
                fontSize: style?.fontSize || item.fontSize || 60,
                color: style?.color || item.color || '#ffffff',
                backgroundColor: style?.backgroundColor || item.backgroundColor,
                fontWeight: style?.fontWeight || item.fontWeight || 'normal',
                textAlign: style?.textAlign || item.textAlign || 'center',
                letterSpacing: style?.letterSpacing ?? item.letterSpacing ?? 0,
                stroke: style?.stroke ?? undefined,
                textShadow: style?.textShadow ?? undefined,
            });

            const capY = style?.captionY;
            if (capY !== undefined && item.transform) {
                // Handle both relative (0.8) and absolute (576) Y positions
                const targetY = (capY > 1 || capY < -1) ? capY : (canvasHeight * capY) - (canvasHeight / 2);
                const targetX = style?.captionX ?? 0;

                _updateItem(item.id, {
                    transform: {
                        ...item.transform,
                        x: targetX,
                        y: targetY,
                        width: style?.width ?? item.transform.width,
                        height: style?.height ?? item.transform.height,
                        opacity: style?.opacity ?? item.transform.opacity,
                        cornerRadius: style?.cornerRadius ?? item.transform.cornerRadius,
                    }
                });
            } else if (item.transform && style) {
                // Apply layout properties even if fixed Y is not defined
                _updateItem(item.id, {
                    transform: {
                        ...item.transform,
                        width: style.width ?? item.transform.width,
                        height: style.height ?? item.transform.height,
                        opacity: style.opacity ?? item.transform.opacity,
                        cornerRadius: style.cornerRadius ?? item.transform.cornerRadius,
                    }
                });
            }
            if ((index + 1) % 24 === 0) {
                const progress = 72 + Math.round(((index + 1) / Math.max(textItems.length, 1)) * 10);
                templateApply.update(progress, '텍스트 스타일을 적용하는 중...');
                await yieldToBrowser();
            }
        }

        // 6. Add Template Elements
        if (!isBasic) {
            templateApply.update(86, '템플릿 요소를 배치하는 중...');
            await yieldToBrowser();

            const currentTracks = getLatestTracks();
            const bgTrack = currentTracks.find(t => t.name === 'Background');
            const overlayTrack = currentTracks.find(t => t.name === 'Overlay');
            const titleTrack1 = currentTracks.find(t => t.name === 'Title1');
            const titleTrack2 = currentTracks.find(t => t.name === 'Title2');

            if (template.background && bgTrack) {
                const bgItem: any = {
                    id: crypto.randomUUID(),
                    trackId: bgTrack.id,
                    from: 0,
                    durationInFrames: duration,
                    type: template.background.type === 'color' ? 'shape' : 'image',
                    label: 'Template Background',
                    transform: { x: 0, y: 0, width: canvasWidth, height: canvasHeight, rotation: 0, opacity: template.background.opacity ?? 1 }
                };
                if (template.background.type === 'color') {
                    bgItem.shapeType = 'rectangle';
                    bgItem.fillColor = template.background.value;
                } else {
                    bgItem.src = template.background.value;
                }
                if (template.background.effects?.length) {
                    bgItem.effects = template.background.effects.map((effect) => ({
                        id: crypto.randomUUID(),
                        effect,
                        enabled: true,
                    }));
                }
                _addItem(bgItem);

                // Removed subtle zoom animation for background image (Content Zoom In Preset)
                // We want the template background to remain static
            }

            if (template.overlay && overlayTrack) {
                const overlayItem: any = {
                    id: crypto.randomUUID(),
                    trackId: overlayTrack.id,
                    from: 0,
                    durationInFrames: duration,
                    type: 'image',
                    src: template.overlay.src,
                    label: 'Template Overlay',
                    transform: { x: 0, y: 0, width: canvasWidth, height: canvasHeight, rotation: 0, opacity: template.overlay!.opacity ?? 1 }
                };
                _addItem(overlayItem);
            }

            if (template.title) {
                // Title Text Mapping logic study
                const isIssue = templateId.startsWith('issue');
                const isSsul = templateId.startsWith('ssul');
                const rawName = latestProject?.name || '';
                const availableBrandProfiles = Array.isArray(latestProject?.input_data?.availableBrandProfiles)
                    ? latestProject.input_data.availableBrandProfiles
                    : [];
                const linkedBrand = latestProject?.brandProfileId
                    ? availableBrandProfiles.find((brand: any) => brand.id === latestProject.brandProfileId)
                    : undefined;
                const resolvedChannelName =
                    linkedBrand?.channelName ||
                    linkedBrand?.channelHandle ||
                    '채널명';

                let title1Value = '';
                let title2Value = '';

                if (rawName) {
                    if (isIssue) {
                        const split = splitIssueHeadline(rawName);
                        title1Value = split.title1;
                        title2Value = split.title2;
                    } else if (isSsul) {
                        title1Value = resolvedChannelName;
                        title2Value = rawName.replace(/\n/g, ' '); // Force single line
                    } else {
                        // Generic fallback: use template's default or split name study
                        const lines = template.title?.text.split('\n') || [];
                        title1Value = lines[0] || '';
                        title2Value = lines[1] || '';
                    }
                } else {
                    // No project name, fallback to template default study
                    const lines = template.title?.text.split('\n') || [];
                    title1Value = lines[0] || '';
                    title2Value = lines[1] || '';
                }

                if (template.title?.isDoubleLine) {
                    [title1Value, title2Value].forEach((text, index) => {
                        const targetTrack = index === 0 ? titleTrack1 : titleTrack2;
                        if (!targetTrack) return;

                        const templateTitle = template.title!;
                        const isSecondary = index === 1;
                        let baseTitleStyle = isSecondary ? (templateTitle.secondaryStyle || templateTitle.style) : templateTitle.style;

                        // Issue Template Title 1 Customization
                        const isTitle1 = index === 0;
                        let finalTitleText = text.trim();

                        if (isIssue && isTitle1) {
                            baseTitleStyle = {
                                ...baseTitleStyle,
                                fontFamily: 'Jua',
                                fontSize: 90,
                                letterSpacing: 0
                            };
                            // Force single line for Title 1 in Issue template
                            finalTitleText = finalTitleText.replace(/\n/g, ' ');
                        }

                        // Only use savedTitle if we are NOT changing layouts, to prevent stale style inheritance
                        const savedTitle1 = isLayoutChange ? undefined : latestTemplateStyles[templateId]?.title1;
                        const savedTitle2 = isLayoutChange ? undefined : latestTemplateStyles[templateId]?.title2;
                        const savedTitle = index === 0 ? savedTitle1 : savedTitle2;

                        const titleItem: TextItem = {
                            id: crypto.randomUUID(),
                            trackId: targetTrack.id,
                            type: 'text',
                            label: `Title${index + 1}`,
                            role: isSsul ? (index === 0 ? 'channel-label' : 'headline') : 'headline',
                            text: finalTitleText,
                            from: 0,
                            durationInFrames: duration,
                            fontFamily: savedTitle?.fontFamily || baseTitleStyle.fontFamily,
                            fontSize: savedTitle?.fontSize || baseTitleStyle.fontSize,
                            fontWeight: savedTitle?.fontWeight || baseTitleStyle.fontWeight as any,
                            color: savedTitle?.color || baseTitleStyle.color,
                            backgroundColor: savedTitle?.backgroundColor || baseTitleStyle.backgroundColor,
                            textAlign: savedTitle?.textAlign || baseTitleStyle.textAlign as any,
                            letterSpacing: savedTitle?.letterSpacing ?? baseTitleStyle.letterSpacing ?? 0,
                            stroke: savedTitle?.stroke || baseTitleStyle.stroke,
                            textShadow: savedTitle?.textShadow || baseTitleStyle.textShadow,
                            transform: {
                                x: savedTitle?.captionX ?? (isSecondary ? (templateTitle.secondaryPosition?.x ?? templateTitle.position.x) : templateTitle.position.x),
                                y: savedTitle?.captionY ?? (isSsul && isLandscape ? (index === 0 ? -450 : -350) : (isSecondary ? (templateTitle.secondaryPosition?.y ?? (templateTitle.position.y + (templateTitle.style.fontSize * 1.2))) : templateTitle.position.y)),
                                width: savedTitle?.width ?? (baseTitleStyle.width || canvasWidth),
                                height: savedTitle?.height ?? (isSecondary ? (templateTitle.secondaryHeight || 180) : (templateTitle.height || 180)),
                                rotation: 0,
                                opacity: savedTitle?.opacity ?? 1,
                            }
                        };
                        const existingItems = getLatestItems().filter(i => i.trackId === targetTrack.id);
                        if (existingItems.length > 0) {
                            existingItems.forEach(existing => {
                                _updateItem(existing.id, {
                                    label: titleItem.label,
                                    role: titleItem.role,
                                    text: titleItem.text,
                                    fontFamily: titleItem.fontFamily,
                                    fontSize: titleItem.fontSize,
                                    fontWeight: titleItem.fontWeight,
                                    color: titleItem.color,
                                    backgroundColor: titleItem.backgroundColor,
                                    textAlign: titleItem.textAlign,
                                    letterSpacing: titleItem.letterSpacing,
                                    stroke: titleItem.stroke,
                                    textShadow: titleItem.textShadow,
                                    transform: {
                                        ...existing.transform,
                                        x: titleItem.transform!.x,
                                        y: titleItem.transform!.y,
                                        width: titleItem.transform!.width,
                                        height: titleItem.transform!.height,
                                        opacity: titleItem.transform!.opacity,
                                    }
                                });
                            });
                        } else {
                            _addItem(titleItem);
                        }
                    });
                } else if (titleTrack1) {
                    const savedTitle = latestTemplateStyles[templateId]?.title1;
                    const titleItem: TextItem = {
                        id: crypto.randomUUID(),
                        trackId: titleTrack1.id,
                        type: 'text',
                        label: 'Title1',
                        role: 'headline',
                        text: title1Value || template.title.text,
                        from: 0,
                        durationInFrames: duration,
                        fontFamily: savedTitle?.fontFamily || template.title.style.fontFamily,
                        fontSize: savedTitle?.fontSize || template.title.style.fontSize,
                        fontWeight: savedTitle?.fontWeight || template.title.style.fontWeight as any,
                        color: savedTitle?.color || template.title.style.color,
                        backgroundColor: savedTitle?.backgroundColor || template.title.style.backgroundColor,
                        textAlign: savedTitle?.textAlign || template.title.style.textAlign as any,
                        letterSpacing: savedTitle?.letterSpacing ?? template.title.style.letterSpacing ?? 0,
                        stroke: savedTitle?.stroke || template.title.style.stroke,
                        textShadow: savedTitle?.textShadow || template.title.style.textShadow,
                        transform: {
                            x: savedTitle?.captionX ?? template.title.position.x,
                            y: savedTitle?.captionY ?? template.title.position.y,
                            width: savedTitle?.width ?? (template.title.style.width || canvasWidth),
                            height: savedTitle?.height ?? (template.title.height || 180),
                            rotation: 0,
                            opacity: savedTitle?.opacity ?? 1,
                        }
                    };

                    const existingItems = getLatestItems().filter(i => i.trackId === titleTrack1.id);
                    if (existingItems.length > 0) {
                        existingItems.forEach(existing => {
                            _updateItem(existing.id, {
                                label: titleItem.label,
                                role: titleItem.role,
                                text: titleItem.text,
                                fontFamily: titleItem.fontFamily,
                                fontSize: titleItem.fontSize,
                                fontWeight: titleItem.fontWeight,
                                color: titleItem.color,
                                backgroundColor: titleItem.backgroundColor,
                                textAlign: titleItem.textAlign,
                                letterSpacing: titleItem.letterSpacing,
                                stroke: titleItem.stroke,
                                textShadow: titleItem.textShadow,
                                transform: {
                                    ...existing.transform,
                                    x: titleItem.transform!.x,
                                    y: titleItem.transform!.y,
                                    width: titleItem.transform!.width,
                                    height: titleItem.transform!.height,
                                    opacity: titleItem.transform!.opacity,
                                }
                            });
                        });
                    } else {
                        _addItem(titleItem);
                    }
                }
            }
        }

        // 7. Update active template
        templateApply.update(97, '마무리하는 중...');
        await yieldToBrowser();
        setActiveTemplateId(templateId);
        templateApply.update(100, '적용 완료');
        await sleep(120);
        } catch (error) {
            log.error('Failed to apply template', { templateId, error });
            toast.error('템플릿 적용에 실패했습니다.');
            throw error;
        } finally {
            useTemplateApplyStore.getState().finish();
        }
    }, [setTracks, _updateItemsTransform, _addItem, _removeItems, _updateItem, setActiveTemplateId]);

    /**
     * Captures the current leader/selected style and applies it to ALL followers.
     * This is the "Apply Changes" button functionality.
     */
    const syncCurrentStyleToAll = useCallback(() => {
        if (!activeTemplateId || activeTemplateId === 'basic') return;

        log.info('Syncing current style to all', { activeTemplateId });
        const template = VIDEO_TEMPLATES.find(t => t.id === activeTemplateId);
        if (!template) return;

        const templateTrackNames = ['Overlay', 'Background', 'Template Overlay', 'Template Background'];

        // 1. Identify Leader (Prioritize selected)
        const selectedIds = useSelectionStore.getState().selectedItemIds;
        const selectedItems = items.filter(i => selectedIds.includes(i.id));
        const sortedItems = [...items].sort((a, b) => a.from - b.from);

        const leaderVisual = selectedItems.find(i =>
            (i.type === 'video' || i.type === 'image') &&
            !templateTrackNames.includes(tracks.find(t => t.id === i.trackId)?.name || '')
        ) || sortedItems.find(i =>
            (i.type === 'video' || i.type === 'image') &&
            !templateTrackNames.includes(tracks.find(t => t.id === i.trackId)?.name || '')
        );

        const leaderText = (selectedItems.find(i => i.type === 'text') || sortedItems.find(i =>
            i.type === 'text' &&
            !TEMPLATE_TRACK_NAMES.includes(tracks.find(t => t.id === i.trackId)?.name || '')
        )) as TextItem | undefined;

        if (!leaderVisual && !leaderText) {
            log.warn('No leader found for sync');
            return;
        }

        // 2. Capture and Save Style
        const capturedVisual = leaderVisual?.transform;
        const leaderTrack = tracks.find(t => t.id === leaderText?.trackId);

        const capturedText = leaderText ? {
            fontFamily: leaderText.fontFamily,
            fontWeight: leaderText.fontWeight,
            color: leaderText.color,
            backgroundColor: leaderText.backgroundColor,
            width: leaderText.transform?.width,
            height: leaderText.transform?.height,
            opacity: (leaderText.transform as any)?.opacity,
            cornerRadius: (leaderText.transform as any)?.cornerRadius,
            captionX: leaderText.transform?.x,
            captionY: leaderText.transform?.y,
            letterSpacing: leaderText.letterSpacing ?? 0,
        } : undefined;

        const currentStyle = templateStyles[activeTemplateId] || {};
        const isTitle1 = leaderTrack?.name === 'Title1';
        const isTitle2 = leaderTrack?.name === 'Title2';

        setTemplateStyle(activeTemplateId, {
            ...currentStyle,
            visual: capturedVisual || currentStyle.visual,
            text: (!isTitle1 && !isTitle2) ? capturedText : currentStyle.text,
            title1: isTitle1 ? capturedText : currentStyle.title1,
            title2: isTitle2 ? capturedText : currentStyle.title2,
        });

        // 3. Propagate to all content items
        const contentItems = items.filter(item => {
            const track = tracks.find(t => t.id === item.trackId);
            return track && !TEMPLATE_TRACK_NAMES.includes(track.name) && (item.type === 'video' || item.type === 'image');
        });

        if (capturedVisual) {
            contentItems.forEach(item => {
                _updateItemsTransform([item.id], {
                    x: capturedVisual.x,
                    y: capturedVisual.y,
                    width: capturedVisual.width,
                    height: capturedVisual.height,
                    rotation: 0,
                    objectFit: (capturedVisual.objectFit || 'cover') as any
                });
            });
        }

        if (capturedText) {
            const leaderTrack = tracks.find(t => t.id === leaderText?.trackId);
            const isTitleTrack = leaderTrack?.name.includes('Title');
            const isCaptionTrack = leaderTrack?.name === 'Caption';

            const textItems = items.filter(item => {
                if (item.type !== 'text') return false;
                const track = tracks.find(t => t.id === item.trackId);
                if (!track) return false;

                // Strict isolation: only sync to items on the EXACT same track as leader
                if ((isTitleTrack || isCaptionTrack) && leaderTrack) {
                    return track.name === leaderTrack.name;
                }

                // Default for other text items: exclude template tracks
                return !TEMPLATE_TRACK_NAMES.includes(track.name);
            }) as TextItem[];

            textItems.forEach(item => {
                _updateItem(item.id, {
                    fontFamily: capturedText.fontFamily,
                    color: capturedText.color,
                    backgroundColor: capturedText.backgroundColor,
                    fontWeight: capturedText.fontWeight,
                    letterSpacing: capturedText.letterSpacing ?? 0,
                    transform: {
                        ...item.transform!,
                        x: capturedText.captionX ?? item.transform!.x,
                        y: capturedText.captionY ?? item.transform!.y,
                        width: capturedText.width ?? item.transform!.width,
                        height: capturedText.height ?? item.transform!.height,
                        opacity: capturedText.opacity ?? item.transform!.opacity,
                        cornerRadius: capturedText.cornerRadius ?? item.transform!.cornerRadius,
                    }
                });
            });
        }
    }, [activeTemplateId, items, tracks, currentProject, setTemplateStyle, _updateItemsTransform, _updateItem]);

    const exportCurrentAsTemplate = useCallback(() => {
        const projectMetadata = currentProject?.metadata || { width: 1080, height: 1920 };
        const canvasWidth = projectMetadata.width;

        const templateTrackNames = ['Template Background', 'Template Overlay', 'Title1', 'Title2'];

        const bgTrack = tracks.find(t => t.name === 'Template Background');
        const bgItem = bgTrack ? items.find(i => i.trackId === bgTrack.id) : null;
        let background: any = undefined;
        if (bgItem) {
            if (bgItem.type === 'shape' && (bgItem as any).shapeType === 'rectangle') {
                background = { type: 'color', value: (bgItem as any).fillColor };
            } else if (bgItem.type === 'image') {
                background = { type: 'image', value: (bgItem as any).src };
            }
        }

        const overlayTrack = tracks.find(t => t.name === 'Template Overlay');
        const overlayItem = overlayTrack ? items.find(i => i.trackId === overlayTrack.id) : null;
        let overlay: any = undefined;
        if (overlayItem && overlayItem.type === 'image') {
            overlay = { src: (overlayItem as any).src, opacity: overlayItem.transform?.opacity ?? 1 };
        }

        const primaryVisuals = items.filter(item => {
            const track = tracks.find(t => t.id === item.trackId);
            return track && !templateTrackNames.includes(track.name) && (item.type === 'video' || item.type === 'image');
        });

        let layout = { x: 0, y: 0, scale: 1, objectFit: 'contain' as MediaFitMode };
        let visualEffects: any[] | undefined = undefined;
        if (primaryVisuals.length > 0) {
            const first = primaryVisuals[0] as VideoItem | ImageItem;
            if (first && first.transform) {
                layout = {
                    x: first.transform.x ?? 0,
                    y: first.transform.y ?? 0,
                    scale: (first.transform.width ?? canvasWidth) / canvasWidth,
                    objectFit: first.transform.objectFit || (first as any).objectFit || 'contain'
                };
            }
            visualEffects = (first.effects || []).map((entry) => entry.effect);
        }

        const titleTrack1 = tracks.find(t => t.name === 'Title1');
        const titleTrack2 = tracks.find(t => t.name === 'Title2');
        const titleItems1 = titleTrack1 ? items.filter(i => i.trackId === titleTrack1.id && i.type === 'text') as TextItem[] : [];
        const titleItems2 = titleTrack2 ? items.filter(i => i.trackId === titleTrack2.id && i.type === 'text') as TextItem[] : [];
        const allTitleItems = [...titleItems1, ...titleItems2];

        let title: any = undefined;
        if (allTitleItems.length > 0) {
            const master = titleItems1[0] || titleItems2[0];
            if (master) {
                const isDoubleLine = titleItems2.length > 0;
                title = {
                    text: allTitleItems.map(ti => ti.text).join('\n'),
                    style: {
                        fontFamily: master.fontFamily,
                        fontSize: master.fontSize,
                        fontWeight: master.fontWeight,
                        color: master.color,
                        secondaryColor: (isDoubleLine && titleItems2[0]) ? titleItems2[0].color : undefined,
                        textAlign: master.textAlign,
                        backgroundColor: master.backgroundColor,
                        letterSpacing: master.letterSpacing ?? 0
                    },
                    position: { x: titleItems1[0]?.transform?.x ?? 0, y: titleItems1[0]?.transform?.y ?? 0 },
                    secondaryPosition: isDoubleLine ? { x: titleItems2[0]?.transform?.x ?? 0, y: titleItems2[0]?.transform?.y ?? 0 } : undefined,
                    isDoubleLine
                };
            }
        }

        const captionItems = items.filter(item => {
            const track = tracks.find(t => t.id === item.trackId);
            return track && !templateTrackNames.includes(track.name) && item.type === 'text';
        }) as TextItem[];

        let textStyle: any = undefined;
        if (captionItems.length > 0) {
            const master = captionItems[0];
            if (master) {
                textStyle = {
                    fontFamily: master.fontFamily,
                    fontSize: master.fontSize,
                    fontWeight: master.fontWeight,
                    color: master.color,
                    backgroundColor: master.backgroundColor,
                    textAlign: master.textAlign,
                    stroke: master.stroke,
                    textShadow: master.textShadow,
                    captionY: master.transform?.y ?? 0.85,
                    letterSpacing: master.letterSpacing ?? 0
                };
            }
        }

        const templateResult = {
            id: 'custom-template-' + Date.now(),
            name: 'Custom Template',
            description: 'Exported from current state',
            layout,
            visualEffects,
            background,
            overlay,
            title,
            textStyle
        };

        const jsonString = JSON.stringify(templateResult, null, 4);
        console.log('--- EXPORTED TEMPLATE JSON ---', jsonString);

        const copyWithFallback = async (text: string) => {
            try {
                // Check if clipboard API is available and allowed
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(text);
                    return true;
                }
                throw new Error('Clipboard API unavailable or insecure context');
            } catch (err) {
                console.warn('Clipboard API failed, trying fallback:', err);
                try {
                    const textArea = document.createElement('textarea');
                    textArea.value = text;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-9999px';
                    textArea.style.top = '0';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    const successful = document.execCommand('copy');
                    document.body.removeChild(textArea);
                    return successful;
                } catch (fallbackErr) {
                    console.error('Fallback copy failed:', fallbackErr);
                    return false;
                }
            }
        };

        copyWithFallback(jsonString).then((success) => {
            if (success) {
                toast.success('Template JSON copied to clipboard!');
            } else {
                // Final fallback: show in a prompt for manual copy
                console.log('Clipboard failed. Showing manual prompt.');
                window.prompt('Clipboard blocked. Please copy this JSON manually:', jsonString);
            }
        });

        return templateResult;
    }, [tracks, items, currentProject]);

    const saveCurrentAsTemplate = useCallback(() => {
        const templateResult = exportCurrentAsTemplate();
        if (templateResult) {
            const name = window.prompt('새 템플릿 이름을 입력하세요:', '나의 커스텀 템플릿');
            if (name) {
                const newTemplate = {
                    ...templateResult,
                    name,
                    id: 'custom-' + Date.now()
                };
                saveCustomTemplate(newTemplate as VideoTemplate);
            }
        }
    }, [exportCurrentAsTemplate, saveCustomTemplate]);

    const registerCurrentToCloud = useCallback(() => {
        const templateResult = exportCurrentAsTemplate();
        if (!templateResult) return;

        const name = window.prompt('시스템 공용 템플릿 이름:', '새로운 시스템 템플릿');
        if (!name) return;

        const finalTemplate = {
            ...templateResult,
            name,
            id: 'sys-' + Date.now()
        };

        // Send to parent (Cenema Dashboard)
        if (window.parent !== window) {
            window.parent.postMessage({
                type: 'REGISTER_TEMPLATE',
                payload: {
                    template: finalTemplate,
                    projectId: currentProject?.id
                }
            }, '*');
            log.info('Sent REGISTER_TEMPLATE message to parent');
        } else {
            alert('이 기능은 Cenema 대시보드 내에서만 작동합니다.');
        }
    }, [exportCurrentAsTemplate, currentProject]);

    /**
     * Specifically syncs text style (subtitles) to ALL other text items.
     * Unlike syncCurrentStyleToAll, this focuses purely on text formatting and position.
     */
    const syncTextStyleToAll = useCallback(() => {
        log.info('Syncing text style to all subtitles');

        // 1. Identify Leader (Prioritize selected)
        const selectedIds = useSelectionStore.getState().selectedItemIds;
        const selectedItems = items.filter(i => selectedIds.includes(i.id));
        const sortedItems = [...items].sort((a, b) => a.from - b.from);

        const leaderText = (selectedItems.find(i => i.type === 'text') || sortedItems.find(i =>
            i.type === 'text' &&
            !TEMPLATE_TRACK_NAMES.includes(tracks.find(t => t.id === i.trackId)?.name || '')
        )) as TextItem | undefined;

        if (!leaderText) {
            log.warn('No leader text item found for sync');
            return;
        }

        // 2. Capture Style
        const capturedStyle = {
            fontFamily: leaderText.fontFamily,
            fontSize: leaderText.fontSize,
            fontWeight: leaderText.fontWeight,
            color: leaderText.color,
            backgroundColor: leaderText.backgroundColor,
            textAlign: leaderText.textAlign,
            stroke: leaderText.stroke,
            textShadow: leaderText.textShadow,
            transform: {
                x: leaderText.transform?.x,
                y: leaderText.transform?.y,
                width: leaderText.transform?.width,
                height: leaderText.transform?.height,
                opacity: (leaderText.transform as any)?.opacity,
                cornerRadius: (leaderText.transform as any)?.cornerRadius,
            },
            letterSpacing: leaderText.letterSpacing ?? 0
        };

        const leaderTrack = tracks.find(t => t.id === leaderText.trackId);
        const isTitle1 = leaderTrack?.name === 'Title1';
        const isTitle2 = leaderTrack?.name === 'Title2';
        const isCaption = leaderTrack?.name === 'Caption';
        const isTitleTrack = isTitle1 || isTitle2;

        if (activeTemplateId && activeTemplateId !== 'basic') {
            const currentStyle = templateStyles[activeTemplateId] || {};
            const textStyleForStore = {
                fontFamily: capturedStyle.fontFamily,
                fontSize: capturedStyle.fontSize,
                fontWeight: capturedStyle.fontWeight,
                color: capturedStyle.color,
                backgroundColor: capturedStyle.backgroundColor,
                textAlign: capturedStyle.textAlign,
                stroke: capturedStyle.stroke,
                textShadow: capturedStyle.textShadow,
                width: capturedStyle.transform.width,
                height: capturedStyle.transform.height,
                opacity: capturedStyle.transform.opacity,
                cornerRadius: capturedStyle.transform.cornerRadius,
                captionX: capturedStyle.transform.x,
                captionY: capturedStyle.transform.y,
            };

            setTemplateStyle(activeTemplateId, {
                ...currentStyle,
                text: isCaption ? textStyleForStore : currentStyle.text,
                title1: isTitle1 ? textStyleForStore : currentStyle.title1,
                title2: isTitle2 ? textStyleForStore : currentStyle.title2,
            });
        }

        const targetItems = items.filter(item => {
            if (item.type !== 'text') return false;
            const track = tracks.find(t => t.id === item.trackId);
            if (!track) return false;

            // Strict isolation: only sync to items on the EXACT same track as leader
            if ((isTitleTrack || isCaption) && leaderTrack) {
                return track.name === leaderTrack.name;
            }

            // Default for other text items: exclude template tracks
            return !TEMPLATE_TRACK_NAMES.includes(track.name);
        }) as TextItem[];

        targetItems.forEach(item => {
            if (item.id === leaderText.id) return; // Skip self

            _updateItem(item.id, {
                fontFamily: capturedStyle.fontFamily,
                fontSize: capturedStyle.fontSize,
                fontWeight: capturedStyle.fontWeight,
                color: capturedStyle.color,
                backgroundColor: capturedStyle.backgroundColor,
                textAlign: capturedStyle.textAlign,
                stroke: capturedStyle.stroke,
                textShadow: capturedStyle.textShadow,
                transform: {
                    ...item.transform!,
                    x: capturedStyle.transform.x ?? item.transform!.x,
                    y: capturedStyle.transform.y ?? item.transform!.y,
                    width: capturedStyle.transform.width ?? item.transform!.width,
                    height: capturedStyle.transform.height ?? item.transform!.height,
                    opacity: capturedStyle.transform.opacity ?? item.transform!.opacity,
                    cornerRadius: capturedStyle.transform.cornerRadius ?? item.transform!.cornerRadius,
                }
            });
        });

        log.info(`Propagated style from ${leaderText.id} to ${targetItems.length - 1} items`);
    }, [items, tracks, _updateItem]);

    /**
     * Helper for developers to export current GUI positions into layouts.ts format.
     * Run `exportLayout()` in the browser console.
     */
    const exportLayout = useCallback(() => {
        const selectedIds = useSelectionStore.getState().selectedItemIds;
        const selectedItems = items.filter(i => selectedIds.includes(i.id));
        const sortedItems = [...items].sort((a, b) => a.from - b.from);

        const projectMetadata = currentProject?.metadata || { width: 1080, height: 1920 };
        const canvasWidth = projectMetadata.width;

        const findItemOnTrack = (name: string) => {
            const track = tracks.find(t => t.name === name);
            return track ? items.find(i => i.trackId === track.id) : undefined;
        };

        const leaderVisual = selectedItems.find(i => (i.type === 'video' || i.type === 'image')) ||
            sortedItems.find(i => (i.type === 'video' || i.type === 'image') && !TEMPLATE_TRACK_NAMES.includes(tracks.find(t => t.id === i.trackId)?.name || ''));
        const leaderCaption = items.find(i => i.type === 'text' && tracks.find(t => t.id === i.trackId)?.name === 'Caption');
        const leaderTitle1 = findItemOnTrack('Title1') as TextItem | undefined;
        const leaderTitle2 = findItemOnTrack('Title2') as TextItem | undefined;

        const formatTransform = (t: any) => ({
            x: t?.x ?? 0,
            y: t?.y ?? 0,
            width: t?.width ?? 1080,
            height: t?.height ?? 150
        });

        const result = {
            id: activeTemplateId?.split('-')[0] || 'manual_export',
            content: leaderVisual?.transform ? {
                ...formatTransform(leaderVisual.transform),
                scale: (leaderVisual.transform.width ?? canvasWidth) / canvasWidth,
                objectFit: leaderVisual.transform.objectFit || 'cover'
            } : 'MISSING',
            caption: leaderCaption?.transform ? {
                position: 'bottom',
                ...formatTransform(leaderCaption.transform)
            } : 'MISSING',
            title: leaderTitle1 ? {
                isDoubleLine: !!leaderTitle2,
                position: 'top',
                ...formatTransform(leaderTitle1.transform),
                secondaryX: leaderTitle2?.transform?.x ?? 0,
                secondaryY: leaderTitle2?.transform?.y ?? -700,
                secondaryWidth: leaderTitle2?.transform?.width ?? 1080,
                secondaryHeight: leaderTitle2?.transform?.height ?? 150
            } : 'MISSING'
        };

        console.log("%c🚀 [Layout Export] Copy this to layouts.ts:", "color: #3b82f6; font-weight: bold; font-size: 14px;");
        console.log(JSON.stringify(result, null, 4));
        alert("Layout code has been printed to the console (F12)!");
        return result;
    }, [items, tracks, activeTemplateId, currentProject]);

    // Expose to window for easy console access
    if (typeof window !== 'undefined') {
        (window as any).exportLayout = exportLayout;
    }

    return {
        applyTemplate,
        syncCurrentStyleToAll,
        syncTextStyleToAll,
        exportCurrentAsTemplate,
        saveCurrentAsTemplate,
        registerCurrentToCloud,
        deleteCustomTemplate,
        exportLayout
    };
}
